import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAsync, useSession, useToast } from '../lib/store';
import type { GuildCard, GuildDirectory, JoinPolicy } from '../lib/types';
import { Bar, Button, EmptyState, ErrorState, LoadingGrid, Modal, Panel } from '../components/ui';
import { formatNumber, timeAgo } from '../lib/format';

/**
 * Crests are *keys*, not glyphs, for the same reason every other art reference
 * in this game is a key: the stored value has to survive a redesign, and a
 * database full of emoji would pin the look forever.
 */
export const CRESTS: Record<string, string> = {
  sprout: '🌱',
  flame: '🔥',
  drop: '💧',
  bolt: '⚡',
  moon: '🌙',
  peak: '⛰',
  leaf: '🍃',
  star: '★',
};

export const crestGlyph = (key: string) => CRESTS[key] ?? CRESTS.sprout!;

const CREST_KEYS = Object.keys(CRESTS);
const BANNERS = ['leaf', 'ember', 'tide', 'dusk', 'gold', 'stone'];

const POLICY_LABEL: Record<JoinPolicy, string> = {
  OPEN: 'Anyone may join',
  APPLY: 'Apply to join',
  INVITE: 'Invitation only',
};

export function GuildsPage() {
  const { refresh } = useSession();
  const { push } = useToast();
  const state = useAsync<GuildDirectory>(() => api.get('/guilds'));
  const [founding, setFounding] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  async function join(guild: GuildCard) {
    setBusy(guild.key);
    try {
      const result = await api.post<{ joined: boolean }>(`/guilds/${guild.key}/join`, {});
      push(
        'success',
        result.joined ? `You joined ${guild.name}!` : `Applied to ${guild.name}.`,
        '⚑',
      );
      state.reload();
      void refresh();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not do that.');
    } finally {
      setBusy(null);
    }
  }

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;

  const mine = state.data?.mine ?? null;
  const others = (state.data?.guilds ?? []).filter((guild) => guild.key !== mine?.key);
  const pending = new Set(state.data?.myRequests.map((row) => row.guildKey));

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Guilds</h1>
          <div className="page-head__sub">
            A guild is a place to talk, and a way to restore the world together faster than anyone
            manages alone.
          </div>
        </div>
        {!mine && (
          <Button variant="go" size="lg" onClick={() => setFounding(true)}>
            Found a guild
          </Button>
        )}
      </div>

      {mine && (
        <Panel title="⚑ Your guild">
          <GuildRow guild={mine} to={`/guilds/${mine.key}`} note={`You are ${mine.myRole?.toLowerCase()}`} />
        </Panel>
      )}

      {(state.data?.myRequests.length ?? 0) > 0 && (
        <Panel title="✉ Waiting on you">
          <div className="stack-sm">
            {state.data!.myRequests.map((row) => (
              <div key={row.guildKey} className="pxwell row row--between" style={{ padding: 8 }}>
                <span className="tiny">
                  {row.kind === 'INVITE' ? (
                    <>
                      <strong>{row.guildName}</strong> invited you
                    </>
                  ) : (
                    <>
                      You applied to <strong>{row.guildName}</strong>
                    </>
                  )}{' '}
                  · {timeAgo(row.createdAt)}
                </span>
                <Link className="btn btn--sm" to={`/guilds/${row.guildKey}`}>
                  Look
                </Link>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {state.loading && !state.data ? (
        <LoadingGrid count={6} height={200} />
      ) : others.length === 0 && !mine ? (
        <EmptyState art="⚑" title="No guilds yet">
          Nobody has founded one. That is an opportunity.
        </EmptyState>
      ) : (
        <Panel title="◈ Every guild" hint={`${others.length} to look at`}>
          <div className="guild-grid">
            {others.map((guild) => (
              <div key={guild.key} className="pxframe pxframe--raised guild-card">
                <Link to={`/guilds/${guild.key}`} style={{ display: 'block', color: 'inherit' }}>
                  <Banner guild={guild} />
                </Link>
                <div className="guild-card__body stack-sm">
                  <div className="row row--between">
                    <Link to={`/guilds/${guild.key}`} style={{ color: 'inherit' }}>
                      <h3>{guild.name}</h3>
                    </Link>
                    <span className="role-chip">Lv {guild.level}</span>
                  </div>
                  <p className="tiny muted" style={{ margin: 0, minHeight: '2.4em' }}>
                    {guild.motto || guild.description || 'No description yet.'}
                  </p>
                  <div className="row row--between tiny mono">
                    <span>
                      {guild.memberCount} / {guild.memberSlots} members
                    </span>
                    <span className="muted">{POLICY_LABEL[guild.joinPolicy]}</span>
                  </div>
                  {!mine && guild.joinPolicy !== 'INVITE' && (
                    <Button
                      size="sm"
                      block
                      variant={guild.joinPolicy === 'OPEN' ? 'go' : 'primary'}
                      disabled={busy === guild.key || pending.has(guild.key) || guild.memberCount >= guild.memberSlots}
                      loading={busy === guild.key}
                      onClick={() => void join(guild)}
                    >
                      {pending.has(guild.key)
                        ? 'Applied'
                        : guild.memberCount >= guild.memberSlots
                          ? 'Full'
                          : guild.joinPolicy === 'OPEN'
                            ? 'Join'
                            : 'Apply'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {founding && (
        <FoundModal
          cost={state.data?.limits.foundingCost ?? 5000}
          onClose={() => setFounding(false)}
          onFounded={() => {
            setFounding(false);
            state.reload();
            void refresh();
          }}
        />
      )}
    </div>
  );
}

export function Banner({ guild, large }: { guild: { bannerStyle: string; iconStyle: string }; large?: boolean }) {
  return (
    <div className={`guild-banner guild-banner--${guild.bannerStyle} ${large ? 'guild-banner--lg' : ''}`}>
      <span className="guild-crest" aria-hidden>
        {crestGlyph(guild.iconStyle)}
      </span>
    </div>
  );
}

function GuildRow({ guild, to, note }: { guild: GuildCard; to: string; note?: string }) {
  return (
    <Link to={to} className="pxwell row" style={{ padding: 10, gap: 'var(--gap-sm)', color: 'inherit' }}>
      <span className="guild-crest" aria-hidden>
        {crestGlyph(guild.iconStyle)}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row row--between">
          <strong>{guild.name}</strong>
          <span className="role-chip">Lv {guild.level}</span>
        </div>
        <div className="tiny muted">
          {guild.memberCount} / {guild.memberSlots} members{note ? ` · ${note}` : ''}
        </div>
        <Bar ratio={guild.memberCount / Math.max(1, guild.memberSlots)} />
      </div>
    </Link>
  );
}

function FoundModal({
  cost,
  onClose,
  onFounded,
}: {
  cost: number;
  onClose: () => void;
  onFounded: () => void;
}) {
  const { me } = useSession();
  const { push } = useToast();
  const [name, setName] = useState('');
  const [motto, setMotto] = useState('');
  const [description, setDescription] = useState('');
  const [policy, setPolicy] = useState<JoinPolicy>('APPLY');
  const [banner, setBanner] = useState(BANNERS[0]!);
  const [crest, setCrest] = useState(CREST_KEYS[0]!);
  const [busy, setBusy] = useState(false);

  const affordable = (me?.wallet.pokeCoins ?? 0) >= cost;

  async function found() {
    setBusy(true);
    try {
      await api.post('/guilds', {
        name,
        motto,
        description,
        joinPolicy: policy,
        bannerStyle: banner,
        iconStyle: crest,
      });
      push('reward', `${name} exists. You lead it.`, '⚑');
      onFounded();
    } catch (caught) {
      push('error', caught instanceof ApiError ? caught.message : 'Could not found that guild.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} labelledBy="found-title" wide>
      <div className="stack">
        <div>
          <h2 id="found-title">Found a guild</h2>
          <p className="tiny muted" style={{ marginTop: 4 }}>
            Costs {formatNumber(cost)} PokeCoins, and you become its leader. You can only be in one
            guild at a time.
          </p>
        </div>

        <Banner guild={{ bannerStyle: banner, iconStyle: crest }} large />

        <div className="stack-sm">
          <label className="field__label" htmlFor="guild-name">
            Name
          </label>
          <input
            id="guild-name"
            className="input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={28}
            placeholder="Pewter Stoneheads"
          />
          <input
            className="input"
            value={motto}
            onChange={(event) => setMotto(event.target.value)}
            maxLength={80}
            placeholder="Motto (optional)"
          />
          <textarea
            className="textarea"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            maxLength={400}
            placeholder="What is this guild for?"
          />
        </div>

        <div className="row row--wrap" style={{ gap: 'var(--gap)' }}>
          <div className="stack-sm">
            <span className="field__label">Banner</span>
            <div className="row" style={{ gap: 4 }}>
              {BANNERS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`guild-banner guild-banner--${option}`}
                  style={{
                    width: 40,
                    height: 28,
                    border: 0,
                    cursor: 'pointer',
                    outline: banner === option ? '3px solid var(--leaf)' : 'none',
                    outlineOffset: 2,
                  }}
                  aria-label={option}
                  onClick={() => setBanner(option)}
                />
              ))}
            </div>
          </div>
          <div className="stack-sm">
            <span className="field__label">Crest</span>
            <div className="row" style={{ gap: 4 }}>
              {CREST_KEYS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className="pxwell"
                  style={{
                    width: 32,
                    height: 32,
                    border: 0,
                    cursor: 'pointer',
                    outline: crest === option ? '3px solid var(--leaf)' : 'none',
                  }}
                  onClick={() => setCrest(option)}
                  aria-label={option}
                >
                  {crestGlyph(option)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="stack-sm">
          <span className="field__label">Who may join</span>
          <div className="row row--wrap" style={{ gap: 4 }}>
            {(Object.keys(POLICY_LABEL) as JoinPolicy[]).map((option) => (
              <Button
                key={option}
                size="sm"
                variant={policy === option ? 'primary' : 'default'}
                onClick={() => setPolicy(option)}
              >
                {POLICY_LABEL[option]}
              </Button>
            ))}
          </div>
        </div>

        <div className="row row--between">
          <span className="tiny muted">
            {affordable ? `${formatNumber(cost)} coins` : 'Not enough coins'}
          </span>
          <div className="row" style={{ gap: 6 }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="go"
              disabled={!affordable || name.trim().length < 3 || busy}
              loading={busy}
              onClick={() => void found()}
            >
              Found it
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
